import { useEffect, useRef, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { useResourceKit } from '@resource-kit/core/react';
import { Label } from '../primitives/Label';
import { cn } from '../../cn';
import { resolveInputWidget } from '../registry/widgets';
import { failHard } from '@resource-kit/core';
import { GENERIC_ISSUE_KEY } from '@resource-kit/controllers';
import { toWidgetField } from '../registry/field-type-registry';
import { RelationInput } from '../relation/RelationInput';
import { RelationValue } from '../relation/RelationValue';
import type { Issue } from '@resource-kit/model';
import type { EntityField } from '@resource-kit/controllers/react';

export type FieldSlotMode = 'readonly' | 'edit';

export interface FieldSlotProps<T> {
  field: EntityField<T>;
  /** Read-only projection of the value (the column's `cell` output). */
  display: ReactNode;
  /** Draft value driving the input. */
  value: unknown;
  mode: FieldSlotMode;
  /** Editing this field is possible at all (permission + `readonly` state). */
  editable: boolean;
  /** A save is in flight — every control is frozen (design §6.2 invariant 3). */
  busy: boolean;
  issues: readonly Issue<T>[];
  /** `compact` = drawer key/value row; `card` = full-page two-column grid. */
  layout: 'compact' | 'card';
  /** Block-level content (raw json/array) stacks label above value. */
  block?: boolean;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  onRequestEdit: () => void;
  /**
   * Bumped by the controller when focus must land here. Stored as a token
   * rather than a boolean so two consecutive requests for the SAME field
   * still re-fire.
   */
  focusToken: number | null;
}

const FOCUSABLE = 'input, textarea, select, [role="combobox"], button, [data-field-value]';

/**
 * One field of a `FormView` (#1797).
 *
 * The outer element is the same DOM node in both modes — `mode` switches what
 * is rendered INSIDE it, never whether the slot exists. That is what keeps
 * scroll position, tab selection and collapse state alive across an edit
 * (design §6.2 invariant 1); rendering two sibling slots and toggling between
 * them would remount and lose all three.
 */
export function FieldSlot<T>({
  field,
  display,
  value,
  mode,
  editable,
  busy,
  issues,
  layout,
  block = false,
  onChange,
  onBlur,
  onRequestEdit,
  focusToken,
}: FieldSlotProps<T>) {
  const { t } = useResourceKit();
  const containerRef = useRef<HTMLDivElement>(null);
  // Keep the historical `form-<key>` control id: it is what `<Label htmlFor>`,
  // the DatePicker test id and the e2e specs already address.
  const fieldId = `form-${String(field.key)}`;
  const issueId = `${fieldId}-issue`;
  const label = t(field.label, { defaultValue: field.label });
  const issue = issues[0];

  useEffect(() => {
    if (focusToken === null) return;
    const control = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    if (!control) return;
    // §6.3: in edit the input takes focus and its text is selected so a
    // retype replaces it; in readonly the field CONTAINER takes it back
    // after a save, so focus never falls to <body>.
    control.focus();
    if (
      mode === 'edit' &&
      (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement)
    ) {
      control.select();
    }
  }, [focusToken, mode]);

  const labelNode = (
    <span
      className={cn('inline-flex items-baseline', layout === 'compact' && !block && 'shrink-0')}
    >
      <Label
        htmlFor={mode === 'edit' ? fieldId : undefined}
        className={cn('text-sm text-muted-foreground', layout === 'card' && 'font-medium')}
      >
        {label}
      </Label>
      {/* Outside the <label> and aria-hidden: the required marker is a
                visual affordance, and folding it into the accessible name
                would rename the control to "Name*". */}
      {field.required && (
        <span aria-hidden="true" className="text-destructive ml-0.5">
          *
        </span>
      )}
    </span>
  );

  // The form control is the field's resolved input WIDGET (#1406, #1799;
  // three-level resolution since #1805) — a component, so a projection may
  // legally use hooks.
  //
  // `input` is optional on `Widget` since ADR-0030, but resolution refills it
  // from the field type's default and a type with no default throws before
  // returning. So `undefined` here cannot mean "this field is uneditable" —
  // it can only mean resolution was bypassed, which is an authoring bug.
  // Rendering nothing would dress that bug up as an empty form.
  const inputWidget = resolveInputWidget(field);
  const FieldInput =
    inputWidget.input ??
    failHard(
      `widget "${field.widget ?? `<type default:${field.type}>`}" behind field ` +
        `"${String(field.key)}" has no input projection; resolution must refill it ` +
        'from the field type default',
    );
  // A many2one takes over BOTH projections of the slot (#1804 batch C): the
  // value is an id into another model, and neither a text box nor a raw uuid
  // is a faithful rendering of that.
  const relation = field.relation;

  const body =
    mode === 'edit' ? (
      <div className="space-y-1">
        {relation ? (
          <RelationInput
            model={relation}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            readOnly={busy || !editable}
            fieldId={fieldId}
            fieldLabel={label}
            invalid={issue != null}
            describedBy={issue ? issueId : undefined}
          />
        ) : (
          <FieldInput
            value={value}
            // The legacy slot is handed a field and a draft value,
            // never the record: a create form has none. A widget
            // that needs one waits for the v2 renderer (#1806).
            record={null}
            field={toWidgetField(field)}
            mode="input"
            onChange={onChange}
            control={{
              id: fieldId,
              label,
              readOnly: busy || !editable,
              invalid: issue != null,
              describedBy: issue ? issueId : undefined,
              onBlur,
            }}
          />
        )}
        {/* Backend and frontend issues render through this one path and
                    are deliberately NOT distinguished (design §5.4). */}
        {issue && (
          <p id={issueId} className="text-xs text-destructive">
            {t(issue.message, { defaultValue: t(GENERIC_ISSUE_KEY) })}
          </p>
        )}
        {field.description && field.type !== 'boolean' && (
          <p className="text-xs text-muted-foreground">{field.description}</p>
        )}
      </div>
    ) : (
      <div className="flex items-center gap-1 min-w-0">
        {/* A relation carries no click-to-edit: its own link already
                    owns the click, and navigating away and opening the editor
                    are different intents. The pencil below stays the edit
                    affordance for these fields. */}
        {relation ? (
          <div className="text-sm text-foreground break-words overflow-hidden min-w-0">
            <RelationValue model={relation} value={value} />
          </div>
        ) : /* Entry point 1: clicking the value itself. */ editable ? (
          /* Deliberately NOT a <button>. `display` is the column's
                       cell output and may legally carry its own controls — the
                       `id` type always renders a CopyButton, several widgets
                       render links — so a <button> wrapper produced nested
                       interactive elements: invalid HTML, and their labels fold
                       into this element's accessible name ("sup-1 Copy ID").
                       The value stays inert content and the pencil beside it is
                       the named, tab-reachable control. `tabIndex={-1}` keeps
                       this node PROGRAMMATICALLY focusable so §6.3 can hand
                       focus back here after a save, without adding a second,
                       unnamed tab stop to every field. */
          <div
            data-field-value
            data-testid={`field-value-${String(field.key)}`}
            tabIndex={-1}
            onClick={onRequestEdit}
            className="text-sm text-foreground break-words overflow-hidden min-w-0 text-left rounded hover:bg-muted/50 px-1 -mx-1 cursor-pointer"
          >
            {display}
          </div>
        ) : (
          <div className="text-sm text-foreground break-words overflow-hidden min-w-0">
            {display}
          </div>
        )}
        {/* Entry point 2: the hover pencil. Same handler, same effect. */}
        {editable && (
          <button
            type="button"
            aria-label={t('rk.resource.editField', { field: label })}
            onClick={onRequestEdit}
            className="opacity-0 focus-visible:opacity-100 group-hover/slot:opacity-100 transition-opacity shrink-0 text-muted-foreground hover:text-foreground"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );

  const stacked = layout === 'card' || block || mode === 'edit';

  return (
    <div
      ref={containerRef}
      data-testid={`field-slot-${String(field.key)}`}
      className={cn(
        'group/slot min-w-0',
        stacked ? 'space-y-1.5 py-2' : 'flex justify-between gap-4 py-2',
        layout === 'compact' && !stacked && 'items-start',
      )}
    >
      {labelNode}
      <div className={cn(stacked ? '' : 'text-right min-w-0')}>{body}</div>
    </div>
  );
}
